var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../Base.js";
import GlobalEvents from "../../GlobalEvents.js";
import DomHelper from "../../helper/DomHelper.js";
import Rectangle from "../../helper/util/Rectangle.js";
var DragHelperContainer_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    //region Init
    /**
     * Initialize container drag mode.
     * @private
     */
    initContainerDrag() {
      const me = this;
      if (!me.mode) {
        me.mode = "container";
      }
      if (me.mode === "container" && !me.containers) {
        throw new Error("Container drag mode must specify containers");
      }
    }
    //endregion
    //region Grab, update, finish
    /**
     * Grab an element which can be dragged between containers.
     * @private
     * @param event
     * @returns {Boolean}
     */
    grabContainerDrag(event) {
      const me = this;
      if (!me.ignoreSelector || !event.target.closest(me.ignoreSelector)) {
        const element = DomHelper.getAncestor(event.target, me.containers, me.outerElement);
        if (element) {
          const box = element.getBoundingClientRect();
          me.context = {
            element,
            valid: true,
            action: "container",
            offsetX: event.pageX - box.left,
            offsetY: event.pageY - box.top,
            originalPosition: {
              parent: element.parentElement,
              prev: element.previousElementSibling,
              next: element.nextElementSibling
            }
          };
        }
        return true;
      }
      return false;
    }
    startContainerDrag(event) {
      var _a2;
      const me = this, { context, floatRootOwner } = me, { element: dragElement } = context, clonedNode = dragElement.cloneNode(true), outerWidgetEl = floatRootOwner == null ? void 0 : floatRootOwner.element.closest(".b-outer");
      clonedNode.classList.add(me.dragProxyCls, me.draggingCls);
      ((floatRootOwner == null ? void 0 : floatRootOwner.floatRoot) || DomHelper.getRootElement(dragElement)).appendChild(clonedNode);
      context.dragProxy = clonedNode;
      context.dragging = dragElement;
      dragElement.classList.add(me.dropPlaceholderCls);
      if ((_a2 = outerWidgetEl == null ? void 0 : outerWidgetEl.parentElement) == null ? void 0 : _a2.matches(".b-float-root")) {
        clonedNode.style.zIndex = floatRootOwner.floatRootMaxZIndex + 1;
      }
    }
    onContainerDragStarted(event) {
      const me = this, { context } = me, { element: dragElement, dragProxy } = context, box = dragElement.getBoundingClientRect();
      if (me.autoSizeClonedTarget) {
        dragProxy.style.width = box.width + "px";
        dragProxy.style.height = box.height + "px";
        DomHelper.setTranslateXY(context.dragProxy, box.left, box.top);
      } else if (me.useCenteredCursor) {
        const proxyBox = dragProxy.getBoundingClientRect();
        Object.assign(context, {
          offsetX: proxyBox.width / 2,
          offsetY: proxyBox.height / 2
        });
        DomHelper.setTranslateXY(dragProxy, event.clientX, event.clientY);
      }
    }
    /**
     * Move the placeholder element into its new position on valid drag.
     * @private
     * @param event
     */
    updateContainerDrag(event) {
      var _a2;
      const me = this, { context } = me, { targetElement } = context;
      if (!context.started || !targetElement) {
        return;
      }
      const containerElement = DomHelper.getAncestor(targetElement, me.containers, "b-gridbase"), willLoseFocus = (_a2 = context.dragging) == null ? void 0 : _a2.contains(DomHelper.getActiveElement(context.dragging));
      if (containerElement && DomHelper.isDescendant(context.element, containerElement)) {
        return;
      }
      if (willLoseFocus) {
        GlobalEvents.suspendFocusEvents();
      }
      if (containerElement && context.valid) {
        me.moveNextTo(containerElement, event);
      } else {
        me.revertPosition();
      }
      if (willLoseFocus) {
        GlobalEvents.resumeFocusEvents();
      }
      event.preventDefault();
    }
    /**
     * Finalize drag, fire drop.
     * @private
     * @param event
     * @fires drop
     */
    finishContainerDrag(event) {
      const me = this, { context } = me, { dragging, dragProxy, valid, draggedTo, insertBefore, originalPosition } = context;
      if (dragging) {
        context.valid = Boolean(valid && (draggedTo || me.externalDropTargetSelector && event.target.closest(me.externalDropTargetSelector)) && // no drop on self or parent
        (dragging !== insertBefore || originalPosition.parent !== draggedTo));
        context.finalize = (valid2 = context.valid) => {
          if (!valid2 && me.context) {
            me.revertPosition();
          }
          dragging.classList.remove(me.dropPlaceholderCls);
          dragProxy.remove();
          me.reset();
        };
        context.async = false;
        me.trigger("drop", { context, event });
        if (!context.async) {
          context.finalize();
        }
      }
    }
    /**
     * Aborts a drag operation.
     * @private
     * @param {Boolean} [invalid]
     * @param {Object} [event]
     * @param {Boolean} [silent]
     */
    abortContainerDrag(invalid = false, event = null, silent = false) {
      const me = this, { context } = me;
      if (context.dragging) {
        context.dragging.classList.remove(me.dropPlaceholderCls);
        context.dragProxy.remove();
        me.revertPosition();
      }
      if (!silent) {
        me.trigger(invalid ? "drop" : "abort", { context, event });
      }
      me.reset();
    }
    //endregion
    //region Helpers
    /**
     * Updates the drag proxy position.
     * @private
     * @param event
     */
    updateContainerProxy(event) {
      var _a2, _b;
      const me = this, { context } = me, proxy = context.dragProxy;
      let newX = event.pageX - context.offsetX, newY = event.pageY - context.offsetY;
      if (typeof me.minX === "number") {
        newX = Math.max(me.minX, newX);
      }
      if (typeof me.maxX === "number") {
        newX = Math.min(me.maxX - proxy.offsetWidth, newX);
      }
      if (typeof me.minY === "number") {
        newY = Math.max(me.minY, newY);
      }
      if (typeof me.maxY === "number") {
        newY = Math.min(me.maxY - proxy.offsetHeight, newY);
      }
      if (me.lockX) {
        DomHelper.setTranslateY(proxy, newY);
      } else if (me.lockY) {
        DomHelper.setTranslateX(proxy, newX);
      } else {
        DomHelper.setTranslateXY(proxy, newX, newY);
      }
      let targetElement;
      if (event.type === "touchmove") {
        const touch = event.changedTouches[0];
        targetElement = DomHelper.elementFromPoint(touch.clientX, touch.clientY);
      } else {
        targetElement = event.target;
      }
      context.targetElement = ((_a2 = me.resolveTargetElement) == null ? void 0 : _a2.call(me, targetElement)) || targetElement;
      if (!((_b = context.targetElement.nextElementSibling) == null ? void 0 : _b.matches(`.${me.dropPlaceholderCls}`))) {
        context.insertBefore = context.targetElement.nextElementSibling;
      }
    }
    /**
     * Positions element being dragged in relation to targetElement.
     * @private
     * @param targetElement
     * @param event
     */
    moveNextTo(targetElement, event) {
      const { context } = this, dragElement = context.dragging, parent = targetElement.parentElement;
      if (targetElement !== dragElement) {
        const centerX = Rectangle.from(targetElement).center.x;
        if (this.isRTL && event.pageX > centerX || !this.isRTL && event.pageX < centerX) {
          parent.insertBefore(dragElement, targetElement);
          context.insertBefore = targetElement;
        } else {
          if (targetElement.nextElementSibling) {
            if (targetElement.nextElementSibling !== dragElement) {
              context.insertBefore = targetElement.nextElementSibling;
              parent.insertBefore(dragElement, targetElement.nextElementSibling);
            } else if (!context.insertBefore && dragElement.parentElement.lastElementChild !== dragElement) {
              context.insertBefore = targetElement.nextElementSibling;
            }
          } else {
            parent.appendChild(dragElement);
            context.insertBefore = null;
          }
        }
        context.draggedTo = parent;
      }
    }
    /**
     * Moves element being dragged back to its original position.
     * @private
     */
    revertPosition() {
      const { context } = this, { dragging } = context, { parent, next } = context.originalPosition;
      if (next) {
        const isNoop = next.previousSibling === dragging || !next && dragging === parent.lastChild;
        if (!isNoop) {
          parent.insertBefore(dragging, next);
        }
      } else {
        parent.appendChild(dragging);
      }
      context.draggedTo = null;
    }
    //endregion
  }, __publicField(_a, "$name", "DragHelperContainer"), _a;
};
export {
  DragHelperContainer_default as default
};
